import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONTRACT_KIND_LABELS, type PublicContract } from '@kontor/shared';
import { api, ApiError } from '../../api';
import '../offers/offers.css';
import './contracts.css';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });

const message = (e: unknown, field?: string) =>
  e instanceof ApiError ? (e.status === 429 ? 'Zu viele Versuche. Bitte warten Sie einen Moment.' : ((field && e.fields[field]) || Object.values(e.fields)[0] || e.message)) : 'Das hat nicht geklappt. Bitte versuchen Sie es noch einmal.';

/**
 * Öffentliche Seite zum Unterschreiben eines Vertrags. Der Vertragspartner erreicht sie über den Link aus der E-Mail, ohne Anmeldung. Er liest den Vertrag, fordert einen Code an seine
 * E-Mail-Adresse an und unterschreibt damit. Das Gerät braucht nichts außer einem Browser, die Seite funktioniert auch auf dem Handy.
 */
export function PublicContractPage() {
  const { token = '' } = useParams();
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [consent, setConsent] = useState(false);
  const [codeSent, setCodeSent] = useState<{ sentTo: string; minutes: number } | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const contract = useQuery({ queryKey: ['public-contract', token], queryFn: async () => (await api<{ data: PublicContract }>('GET', `/public/contracts/${token}`)).data, retry: false });
  const requestCode = useMutation({
    mutationFn: async () => (await api<{ data: { sentTo: string; minutes: number } }>('POST', `/public/contracts/${token}/code`, { name: name.trim() })).data,
    onSuccess: (r) => {
      setCodeSent(r);
      setError(null);
    },
    onError: (e) => setError(message(e, 'name')),
  });
  const sign = useMutation({
    mutationFn: async () => (await api<{ data: PublicContract }>('POST', `/public/contracts/${token}/sign`, { name: name.trim(), code: code.trim(), consent })).data,
    onSuccess: (c) => client.setQueryData(['public-contract', token], c),
    onError: (e) => setError(message(e, 'code')),
  });
  const decline = useMutation({
    mutationFn: async () => (await api<{ data: PublicContract }>('POST', `/public/contracts/${token}/decline`, { reason: reason.trim() || undefined })).data,
    onSuccess: (c) => client.setQueryData(['public-contract', token], c),
    onError: (e) => setError(message(e)),
  });

  if (contract.isLoading) return <div className="center-screen">Wird geladen …</div>;
  if (contract.isError || !contract.data) {
    return (
      <div className="center-screen">
        <div className="card narrow">
          <h1>Vertrag nicht verfügbar</h1>
          <p className="muted">Der Link ist ungültig oder wurde zurückgezogen. Bitte wenden Sie sich an das Unternehmen, das Ihnen den Vertrag geschickt hat.</p>
        </div>
      </div>
    );
  }
  const c = contract.data;

  const request = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length < 2) return setError('Bitte geben Sie Ihren Namen an.');
    requestCode.mutate();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code.trim())) return setError('Der Code besteht aus sechs Ziffern.');
    if (!consent) return setError('Bitte bestätigen Sie die Erklärung mit dem Häkchen.');
    sign.mutate();
  };

  return (
    <div className="public-offer">
      <header className="public-offer__head"><strong>{c.companyName}</strong></header>
      <main className="public-offer__main">
        <div className="card">
          <div className="muted small">{CONTRACT_KIND_LABELS[c.kind]} · {c.number}</div>
          <h1>{c.title}</h1>
          <p className="muted small">für {c.partyName}</p>
          <pre className="contract-body contract-body--box">{c.body}</pre>
          <p><a className="btn btn--secondary" href={`/api/v1/public/contracts/${token}/pdf`} target="_blank" rel="noreferrer">PDF herunterladen</a></p>
        </div>

        {c.status === 'signed' && (
          <div className="alert alert--ok" role="status">
            Der Vertrag ist{c.signedAt ? ` seit ${stamp.format(new Date(c.signedAt))}` : ''} unterschrieben. Sie haben das unterschriebene PDF mit dem Protokoll per E-Mail erhalten. Hier können Sie es auch herunterladen.
          </div>
        )}
        {c.status === 'declined' && <div className="alert alert--warn" role="status">Sie haben diesen Vertrag abgelehnt. Es wurde nichts unterschrieben.</div>}
        {c.status === 'expired' && <div className="alert alert--warn" role="status">Die Frist zum Unterschreiben ist abgelaufen. Bitten Sie {c.companyName}, Ihnen den Vertrag erneut zu senden.</div>}
        {c.status === 'void' && <div className="alert alert--warn" role="status">Dieser Vertrag wurde zurückgezogen und kann nicht mehr unterschrieben werden.</div>}

        {c.status === 'sent' && (
          <div className="card">
            <h2>Online unterschreiben</h2>
            {error && <div className="alert alert--error" role="alert">{error}</div>}

            {!codeSent ? (
              <form className="contract-sign" onSubmit={request} noValidate>
                <p className="muted">Zum Unterschreiben bestätigen Sie Ihre Identität mit einem Code, den wir an Ihre E-Mail-Adresse senden. Geben Sie dazu Ihren vollständigen Namen an.</p>
                <label className="field">
                  <span>Ihr vollständiger Name</span>
                  <input className="input" value={name} autoComplete="name" maxLength={160} onChange={(e) => setName(e.target.value)} />
                </label>
                <div className="form-actions">
                  <button className="btn btn--primary" type="submit" disabled={requestCode.isPending}>{requestCode.isPending ? 'Wird gesendet …' : 'Code an meine E-Mail senden'}</button>
                  {!declining && <button className="btn btn--ghost" type="button" onClick={() => setDeclining(true)}>Nicht unterschreiben</button>}
                </div>
              </form>
            ) : (
              <form className="contract-sign" onSubmit={submit} noValidate>
                <p>Wir haben einen Code an <strong>{codeSent.sentTo}</strong> gesendet. Er gilt {codeSent.minutes} Minuten.</p>
                <label className="field">
                  <span>Code aus der E-Mail</span>
                  <input className="input contract-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
                </label>
                <label className="check">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                  <span>{c.statement}</span>
                </label>
                <div className="form-actions">
                  <button className="btn btn--primary" type="submit" disabled={sign.isPending}>{sign.isPending ? 'Wird unterschrieben …' : 'Verbindlich unterschreiben'}</button>
                  <button className="btn btn--secondary" type="button" disabled={requestCode.isPending} onClick={() => requestCode.mutate()}>Neuen Code senden</button>
                  {!declining && <button className="btn btn--ghost" type="button" onClick={() => setDeclining(true)}>Nicht unterschreiben</button>}
                </div>
              </form>
            )}

            {declining && (
              <div className="contract-sign">
                <h3>Vertrag ablehnen</h3>
                <label className="field">
                  <span>Grund (freiwillig)</span>
                  <input className="input" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
                </label>
                <div className="form-actions">
                  <button className="btn btn--danger" type="button" disabled={decline.isPending} onClick={() => decline.mutate()}>Vertrag ablehnen</button>
                  <button className="btn btn--secondary" type="button" onClick={() => setDeclining(false)}>Abbrechen</button>
                </div>
              </div>
            )}
          </div>
        )}
        {c.status === 'sent' && c.expiresAt && <p className="muted small">Sie können bis {stamp.format(new Date(c.expiresAt))} unterschreiben.</p>}
      </main>
    </div>
  );
}
